import { Box, Button, styled } from '@mui/material'
import { useEffect } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { Route, RouteParameters } from '../../../../constants'
import { useIsFitterSchedulingMode } from '../../../../hooks'
import { useDoubleBookingContext } from '../../../../hooks/useDoubleBookingContext'
import { AppointmentFormData } from '../../../../types'
import { formDataToStartDate, hydratePathname, toSafeFormData } from '../../../../util'
import { SubmitButton } from '../../../forms'
import { ScheduleContent } from '../../../ScheduleContent'
import { AcfuExtendedWindowReason } from '../../AcfuExtendedWindowReason'
import { useFormActionMode } from '../../useFormActionMode'
import { Container, Gap, Section, VerticalRule } from '../Layout'
import { Configuration } from './Configuration'
import { Day } from './Day'
import { Providers } from './Providers'
import { Time } from './Time'

interface Props {
  debug?: boolean
}

const DateSelector = () => (
  <>
    <Section gridArea="day">
      <Day />
    </Section>
    <VerticalRule />
    <Section gridArea="time">
      <Time />
    </Section>
  </>
)

const Content = ({ debug }: Props) => {
  const { clearErrors, formState, reset, setValue, watch } = useFormContext<AppointmentFormData>()
  const { isSubmitSuccessful } = formState
  const appointment = watch()
  const isFitterSchedulingMode = useIsFitterSchedulingMode()
  const doubleBookingContext = useDoubleBookingContext()
  const formActionMode = useFormActionMode()
  const navigate = useNavigate()

  const [clientStartDate, clientStartTime, customer_timezone] = useWatch({
    name: ['clientStartDate', 'clientStartTime', 'customer_timezone'],
  })

  useEffect(() => {
    const startDate = formDataToStartDate({ clientStartDate, clientStartTime, customer_timezone })

    if (clientStartTime) {
      clearErrors('clientStartTime')
    }

    setValue('start_date', startDate)
  }, [clientStartDate, clientStartTime, customer_timezone, setValue])

  useEffect(() => {
    if (!isSubmitSuccessful) return

    // country is preserved here via the ...appointment spread — it retains the value
    // originally set in useScheduleFormDefaultValues and is not explicitly reset to null.
    const emptyFormData: AppointmentFormData = toSafeFormData({
      ...appointment,
      clientStartDate: null,
      clientStartTime: null,
      resource_ids: null,
      start_date: null,
    })

    reset(emptyFormData)
  }, [isSubmitSuccessful, reset])

  const Actions = styled(Box)`
    display: flex;
    justify-content: flex-end;
    padding-top: 1rem;
    width: 100%;
  `

  const handleRescheduleButton = () => {
    const pathname = hydratePathname(Route.APPOINTMENT_RESCHEDULE, {
      [RouteParameters.COUNTRY]: doubleBookingContext.previouslyConfirmedAppointment?.country,
      [RouteParameters.APPOINTMENT_ID]: doubleBookingContext.previouslyConfirmedAppointment?.id,
    })

    navigate(pathname, { replace: true })
  }

  return (
    <Providers>
      <Container isCondensed={isFitterSchedulingMode}>
        <Section gridArea="configuration">
          <Configuration />
        </Section>
        <VerticalRule />
        <ScheduleContent>
          <DateSelector />
        </ScheduleContent>
        {!isFitterSchedulingMode && <Gap style={{ gridArea: 'gap' }} />}
      </Container>
      <AcfuExtendedWindowReason />
      <Actions>
        {formActionMode === 'schedule' && <SubmitButton />}
        {formActionMode === 'reschedule' && (
          <Button
            variant="outlined"
            color="secondary"
            sx={{ position: 'absolute', right: '0px', bottom: '0px' }}
            onClick={handleRescheduleButton}
          >
            Reschedule
          </Button>
        )}
      </Actions>
      {debug && <pre>{JSON.stringify(appointment, null, 2)}</pre>}
    </Providers>
  )
}

export { Content }
